import { useEffect } from "react";
import { publicMediaSrc, toConfiguredAbsoluteUrl } from "../config/api";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";
const PRODUCT_JSON_LD_ID = "product-jsonld";
const BREADCRUMB_JSON_LD_ID = "product-breadcrumb-jsonld";

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.productSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.productSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.productSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.productSeo = "true";
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function oneLine(value) {
  return cleanText(value).replace(/\s+/g, " ").trim();
}

function meaningfulIdentity(value) {
  const text = cleanText(value);
  if (!text) return "";
  const compact = text.toLowerCase().replace(/[.\s/_-]/g, "");
  if (["na", "n/a", "generic", "none", "undefined", "null", "-"].includes(text.toLowerCase())) return "";
  if (compact === "na") return "";
  return text;
}

function productShortDescription(product) {
  return oneLine(product?.shortDescription) || oneLine(product?.description);
}

export function productPageTitle(product, contact) {
  const seoTitle = cleanText(product?.seoTitle);
  if (seoTitle) return seoTitle;
  const name = cleanText(product?.productName);
  const siteName = cleanText(contact?.siteName);
  if (name && siteName) return `${name} Supplier Malaysia | ${siteName}`;
  if (name) return `${name} Supplier Malaysia`;
  if (siteName) return `Product | ${siteName}`;
  return "Product";
}

export function productPageDescription(product, contact) {
  const seoDescription = oneLine(product?.seoDescription);
  if (seoDescription) return seoDescription;
  const shortDescription = productShortDescription(product);
  if (shortDescription) return shortDescription;
  const name = cleanText(product?.productName);
  const siteName = cleanText(contact?.siteName);
  if (name && siteName) {
    return `Enquire about ${name} supplied by ${siteName} for construction and industrial requirements across Malaysia.`;
  }
  if (name) {
    return `Enquire about ${name} for construction and industrial requirements across Malaysia.`;
  }
  if (siteName) {
    return `Enquire about products supplied by ${siteName} for construction and industrial requirements across Malaysia.`;
  }
  return "Enquire about industrial supplies for construction and industrial requirements across Malaysia.";
}

export function productCanonicalPath(categorySlug, productSlug) {
  const category = cleanText(categorySlug);
  const slug = cleanText(productSlug);
  if (!category || !slug) return "";
  return `/category/${category}/${slug}`;
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function clearProductSeoTags() {
  document.head.querySelectorAll("[data-product-seo='true']").forEach((el) => el.remove());
  upsertJsonLd(PRODUCT_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

function ProductSeo({ contact, product, categorySlug, status }) {
  useEffect(() => {
    const siteName = cleanText(contact?.siteName);

    if (status === "loading") {
      clearProductSeoTags();
      document.title = siteName ? `Loading Product | ${siteName}` : "Loading Product";
      upsertMeta("name", "robots", "noindex, nofollow");
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearProductSeoTags();
      };
    }

    if (status === "error" || !product?.slug || product.isPublished === false) {
      clearProductSeoTags();
      if (status === "error") {
        document.title = siteName ? `Unable to load product | ${siteName}` : "Unable to load product";
      }
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearProductSeoTags();
      };
    }

    const name = cleanText(product.productName) || "Product";
    const categoryName = cleanText(product.category?.categoryName);
    const categoryFromProduct = cleanText(product.category?.slug);
    const path = productCanonicalPath(categoryFromProduct || categorySlug, product.slug);
    const pageTitle = productPageTitle(product, contact);
    const pageDescription = productPageDescription(product, contact);
    const canonicalUrl = toConfiguredAbsoluteUrl(path);
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const listUrl = toConfiguredAbsoluteUrl("/category");
    const categoryUrl = categoryFromProduct || cleanText(categorySlug)
      ? toConfiguredAbsoluteUrl(`/category/${categoryFromProduct || categorySlug}`)
      : listUrl;
    const imagePath = publicMediaSrc(product.image);
    const imageUrl = imagePath ? toConfiguredAbsoluteUrl(imagePath) : "";
    const imageAlt =
      cleanText(product.imageAlt) || (name && siteName ? `${name} supplied by ${siteName}` : name);
    const brand = meaningfulIdentity(product.brand);
    const sku = meaningfulIdentity(product.sku);
    const modelNumber = meaningfulIdentity(product.modelNumber);
    const jsonDescription = productShortDescription(product) || pageDescription;

    document.title = pageTitle;
    upsertMeta("name", "robots", "");
    upsertMeta("name", "description", pageDescription);
    upsertLink("canonical", canonicalUrl);
    upsertMeta("property", "og:type", "product");
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", pageDescription);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? imageAlt : "");
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", pageDescription);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? imageAlt : "");

    const crumbs = [
      { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
      { "@type": "ListItem", position: 2, name: "Product Categories", item: listUrl },
    ];
    if (categoryName && categoryUrl) {
      crumbs.push({ "@type": "ListItem", position: 3, name: categoryName, item: categoryUrl });
      crumbs.push({ "@type": "ListItem", position: 4, name, item: canonicalUrl });
    } else {
      crumbs.push({ "@type": "ListItem", position: 3, name, item: canonicalUrl });
    }

    upsertJsonLd(
      PRODUCT_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "Product",
        name,
        description: jsonDescription,
        image: imageUrl ? [imageUrl] : undefined,
        url: canonicalUrl,
        category: categoryName || undefined,
        sku: sku || undefined,
        mpn: modelNumber || undefined,
        brand: brand ? { "@type": "Brand", name: brand } : undefined,
      })
    );
    upsertJsonLd(
      BREADCRUMB_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs,
      })
    );

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      clearProductSeoTags();
    };
  }, [contact, product, categorySlug, status]);

  return null;
}

export default ProductSeo;
